import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { SuporteService } from '@/services/suporte-service'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Search, LogOut, User, BookOpen, Download, Copy, ExternalLink, Loader2, Image as ImageIcon } from 'lucide-react'
import { showSuccess, showError } from '@/utils/toast'

export const Route = createFileRoute('/suporte')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    if (!SuporteService.verificarAcesso()) {
      throw redirect({ to: '/suporte-login' })
    }
  },
  component: SuporteDashboard,
})

function SuporteDashboard() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [clientes, setClientes] = useState<any[]>([])
  const [selectedCliente, setSelectedCliente] = useState<any | null>(null)
  const [livros, setLivros] = useState<any[]>([])
  const [isLoadingLivros, setIsLoadingLivros] = useState(false)

  const handleLogout = () => {
    SuporteService.removerAcesso()
    navigate({ to: '/suporte-login' })
  }

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!query.trim()) return

    setIsSearching(true)
    setSelectedCliente(null)
    setLivros([])
    
    try {
      const results = await SuporteService.buscarClientes(query)
      setClientes(results)
      if (results.length === 0) {
        showError('Nenhum cliente encontrado.')
      }
    } catch (err) {
      showError('Erro ao buscar clientes.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectCliente = async (cliente: any) => {
    setSelectedCliente(cliente)
    setIsLoadingLivros(true)
    try {
      const results = await SuporteService.buscarLivrosDoCliente(cliente.id || cliente.id_cliente)
      setLivros(results)
    } catch (err) {
      showError('Erro ao buscar os livros do cliente.')
    } finally {
      setIsLoadingLivros(false)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    showSuccess(`${label} copiado!`)
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background">
      {/* Header Support */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-10 shadow-md flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-2 rounded-lg">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-lg leading-tight">Sabhia Admin</h1>
            <p className="text-xs text-slate-400">Painel de Suporte</p>
          </div>
        </div>
        <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </header>

      <main className="container mx-auto p-4 sm:p-6 grid md:grid-cols-[350px_1fr] gap-6">
        {/* Sidebar de Busca */}
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/30">
              <h2 className="font-semibold flex items-center gap-2">
                <Search className="w-4 h-4 text-muted-foreground" /> Buscar Cliente
              </h2>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input 
                  placeholder="Nome ou Email..." 
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" disabled={isSearching || !query.trim()}>
                  {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                </Button>
              </form>

              <div className="space-y-2 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                {clientes.map(cliente => (
                  <div 
                    key={cliente.id || cliente.id_cliente}
                    onClick={() => handleSelectCliente(cliente)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedCliente?.email === cliente.email ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-muted/50'}`}
                  >
                    <p className="font-medium truncate text-sm">{cliente.nome || 'Sem nome'}</p>
                    <p className="text-xs text-muted-foreground truncate">{cliente.email}</p>
                  </div>
                ))}
                {!isSearching && query && clientes.length === 0 && (
                  <p className="text-sm text-center text-muted-foreground py-4">Nenhum resultado.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Área Principal (Livros do Cliente) */}
        <div>
          {selectedCliente ? (
            <div className="space-y-6">
              {/* Header do Cliente Selecionado */}
              <Card className="shadow-sm overflow-hidden">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-6 py-4 border-b border-blue-100 dark:border-blue-800/50 flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      {selectedCliente.nome || 'Cliente Sem Nome'}
                    </h2>
                    <p className="text-muted-foreground mt-1">{selectedCliente.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-1">ID Mágico (Hub Login)</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-white dark:bg-black px-2 py-1 rounded border">
                        {selectedCliente.hub_login || 'Sem ID'}
                      </code>
                      {selectedCliente.hub_login && (
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => copyToClipboard(`https://livros.sabhia.com.br/auth/${selectedCliente.hub_login}`, 'Link Mágico')}>
                          <Copy className="w-3 h-3 mr-1" /> Copiar Link
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Livros do Cliente ({livros.length})
                  </h3>

                  {isLoadingLivros ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
                  ) : livros.length > 0 ? (
                    <div className="grid gap-4">
                      {livros.map(livro => (
                        <div key={livro.id} className="p-4 border rounded-lg flex flex-col sm:flex-row gap-4 justify-between bg-card hover:shadow-md transition-shadow">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-lg line-clamp-1" title={livro.titulo}>{livro.titulo}</h4>
                              <Badge variant={livro.status_entrega ? 'success' : 'secondary'} className={livro.status_entrega ? "bg-green-100 text-green-800 border-green-200" : ""}>
                                {livro.status_entrega ? 'Entregue' : 'Produção'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1 mb-3">ID Pedido: {livro.id_pedido}</p>
                            
                            <div className="flex flex-wrap gap-2">
                              {livro.link_pdf ? (
                                <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => window.open(livro.link_pdf, '_blank')}>
                                  <Download className="w-3 h-3" /> PDF
                                </Button>
                              ) : (
                                <Badge variant="outline" className="text-xs text-muted-foreground">Sem PDF</Badge>
                              )}

                              {livro.link_editavel && (
                                <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => window.open(livro.link_editavel, '_blank')}>
                                  <ExternalLink className="w-3 h-3" /> Editável
                                </Button>
                              )}

                              {livro.capa?.imagem_capa && (
                                <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => window.open(livro.capa.imagem_capa, '_blank')}>
                                  <ImageIcon className="w-3 h-3" /> Capa (JPG)
                                </Button>
                              )}

                              {livro.capa?.imagem_capa_png && (
                                <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => window.open(livro.capa.imagem_capa_png, '_blank')}>
                                  <ImageIcon className="w-3 h-3" /> Capa (PNG)
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg bg-muted/10">
                      <p className="text-muted-foreground">Este cliente não possui livros vinculados a ele.</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          ) : (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 border-2 border-dashed rounded-xl text-muted-foreground bg-muted/10">
              <Search className="w-12 h-12 mb-4 opacity-20" />
              <p className="text-lg font-medium">Nenhum cliente selecionado</p>
              <p className="text-sm">Busque e selecione um cliente na barra lateral para visualizar seus livros e links de download.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}